import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { usePopup } from '../common/Popup';

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;

const formatFileSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Container = styled.div`
  width: 100%;
  max-width: 720px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 40px 70px 20px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 35px auto 0 auto;
`;

const Title = styled.h2`
  text-align: center;
  color: #f7921e;
  font-weight: 800;
  font-size: 1.1rem;
  margin-bottom: 30px;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FlexContainer = styled.div`
  display: flex;
  gap: 20px; /* Espaçamento entre os campos */
  width: 100%;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: ${({ flex }) => flex || 1}; /* Flexibilidade para controlar o tamanho dos campos */
`;

const InputLabel = styled.p`
  font-weight: bold;
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  font-size: 16px;
  border: 1px solid #ccc;
  border-radius: 8px;
`;

const UploadContainer = styled.div`
  width: 100%;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  color: #000000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-size: 14px;
`;

const UploadButton = styled.label`
  background-color: #f08c00;
  color: white;
  font-size: 16px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  padding: 8px 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: #e07a00;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin: 20px auto 20px auto;
`;

const Button = styled.button`
  background-color: #6ab292;
  color: white;
  font-size: 16px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  padding: 8px 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: #5a9e82;
  }
`;

const StyledParagraph = styled.p`
  color: #6AB8A8;
  font-size: 14px;
`;

const StyledInput = styled.p`
  color: #8E9B9E;
  font-size: 12px;
`;

const PreencherDados = () => {
  const navigate = useNavigate();
  const popup = usePopup();
  const [numeroCooperacao, setNumeroCooperacao] = useState('');
  const [vigenciaTermo, setVigenciaTermo] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const todayDate = getTodayDate();

  const handleBefore = () => {
    navigate('/TermoCooperacao');
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files?.[0];

    if (selected && selected.size > MAX_FILE_SIZE_BYTES) {
      setFile(null);
      e.target.value = '';
      await popup.alert({
        icon: 'warning',
        title: 'Arquivo muito grande',
        message: `O arquivo selecionado possui ${formatFileSize(selected.size)}. O tamanho máximo permitido é 2 MB.`,
        confirmText: 'Selecionar outro arquivo',
      });
      return;
    }

    setFile(selected || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!numeroCooperacao || !vigenciaTermo || !file) {
      setError('Preencha todos os campos e selecione o PDF.');
      return;
    }
    if (vigenciaTermo < todayDate) {
      setError('A vigência do termo não pode ser anterior à data atual.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.uploadTermoCooperacao(file);

      localStorage.setItem('termoUploadResponse', JSON.stringify({
        numeroCooperacao,
        vigenciaTermo,
        ...response
      }));
      
      navigate('/CadastroInstituicao');
    } catch (err) {
      const message = err.message || 'Falha ao enviar termo.';
      if (/muito grande|tamanho máximo|request file too large/i.test(message)) {
        setFile(null);
        await popup.alert({
          icon: 'warning',
          title: 'Arquivo muito grande',
          message: 'Não foi possível enviar o arquivo. O tamanho máximo permitido é 2 MB.',
          confirmText: 'Entendi',
        });
      } else {
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>PRENCHA OS DADOS DO TERMO DE COOPERAÇÃO TÉCNICA</Title>

      <Form onSubmit={handleSubmit}>
        <FlexContainer>
          <InputWrapper flex={2}>
            <InputLabel>Número de cooperação técnica (TCT)*</InputLabel>
            <Input type="text" value={numeroCooperacao} onChange={(e) => setNumeroCooperacao(e.target.value)} />
          </InputWrapper>

          <InputWrapper flex={1}>
            <InputLabel>Vigência do termo*</InputLabel>
            <Input
              type="date"
              value={vigenciaTermo}
              min={todayDate}
              onChange={(e) => setVigenciaTermo(e.target.value)}
            />
          </InputWrapper>
        </FlexContainer>

        <StyledParagraph>Anexe o Termo de Cooperação Técnicas</StyledParagraph>
        <UploadContainer>
          <span className="material-symbols-outlined" style={{ fontSize: '40px' }}>upload_file</span>
          <p>Arraste o seu arquivo até aqui</p>
          <StyledInput>ou selecione um arquivo do seu computador</StyledInput>
          <UploadButton htmlFor="file-upload">
            Selecionar
            <input id="file-upload" type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: 'none' }} />
          </UploadButton>
          <StyledInput>Formato PDF, com tamanho máximo de 2 MB.</StyledInput>
          {file && (
            <StyledInput>
              Selecionado: {file.name} ({formatFileSize(file.size)})
            </StyledInput>
          )}
        </UploadContainer>
        {error && <StyledInput style={{ color: '#c0392b' }}>{error}</StyledInput>}
        <ButtonContainer>
          <Button type="button" onClick={handleBefore}><span class="material-symbols-outlined" style={{ fontSize: '15px' }}>arrow_back_ios</span>Voltar</Button >
          <Button type="submit" disabled={submitting}>{submitting ? 'Enviando...' : 'Próximo'}  <span class="material-symbols-outlined" style={{ fontSize: '15px'}}> arrow_forward_ios</span></Button>
        </ButtonContainer>
      </Form>
    </Container>
  );
};

export default PreencherDados;
